/* eslint-disable prefer-const */
import Autocomplete from '@material-ui/lab/Autocomplete';
import { TextField, Checkbox, CircularProgress, FormControlLabel, LinearProgress, Tooltip } from '@material-ui/core';
import Chip from '@material-ui/core/Chip';
import { CheckBoxOutlineBlank, CheckBox } from '@material-ui/icons';
import React, { useCallback, useMemo, useRef } from 'react';
import { List } from 'react-virtualized';
import { useState, useEffect } from 'react';
import { debounce, get } from 'lodash';
import { getQueriesPagesData, getTopQueriesORPages, getCampaignKeywordsData } from '../../../utility/common';
import { useSchema } from '../../../utility/services/customHooks';

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;
let prevSearchText;

export default function FormAutoComplete(props) {
  const {
    onChange,
    options,
    value,
    label,
    multiple,
    customTags,
    onTextBlur,
    prevSelected,
    disableAndUngroupChecker,
    containsHierarchie,
    displayTextOnChips = false,
    useKeyForLabel = 'value',
    fullWidth,
    searchByText,
    withCheckBoxes,
    hideLabel,
    useFilterString,
    isSourceFilter,
    showAlternatePlaceholderWithoutOpen,
    disableCloseOnSelect,
    loading,
    onClose,
    alternatePlaceholder,
    type,
    onClearViaChips,
    showProgress,
    groupCheckBox,
    disabled,
    disableFetch,
    isArrayOpType,
    disableClearable,
    stopTyping = false,
    variant,
    className,
    EnableautoFocus = false,
    isInternalLinking = false,
    isCategoryFilter = false,
    keyForProgressBar = 'totalclicks',
    labelForProgressBar = '',
    showProgressBarWithDirectValue = true,
    disableVirtualization = false,
    // Opt-in: let Backspace on an empty input remove the last chip (standard Autocomplete behaviour).
    // Off by default — Backspace removals have been ignored app-wide since P-112 and other consumers
    // rely on that.
    removeChipsOnBackspace = false,
    // Opt-in for single-select fields: show the selected value as a chip, the way multi-select
    // fields do, and keep the input free for searching. The chip has no remove control, so this is
    // meant for fields that always hold a value.
    singleValueAsChip = false,
    // Opt-in for a form of pickers that should all read alike (the trends screens' Basic Filters):
    // the label always above the box, this hint in the box while nothing is picked (e.g. "Any
    // device"), and the down arrow of the form's dropdowns at the right of the box.
    emptyHint,
    // Opt-in for a list with checkboxes: a line of text under each option's name, from the option
    // (e.g. the filters a saved segment holds). The rows then grow with it, so the long-list
    // virtualization, which needs one row height, stays off.
    getOptionNote,
  } = props;
  const [isOpen, setOpen] = useState(false);
  const [focusedIndex, setIndex] = useState(0);
  const shouldScrollToIndexRef = useRef(false);
  const schemaName = useSchema();
  const [optionsData, setOptionsData] = useState(options);
  const [loadingOptions, setLoadingOptions] = useState(loading);
  const useSimpleExplores = props?.id?.toLowerCase()?.includes('growth_trends') ?? false;
  if (showProgress) {
    const OptionArray = Object.keys(options).map(function (key) {
      return Number(options[key]?.[keyForProgressBar]) || 0;
    });
    var max = Math.max(...OptionArray);
  }
  const [searchValue, setSearchValue] = useState('');

  // Memoize ListboxComponent to prevent recreation on every render
  const ListboxComponent = useMemo(() => {
    return React.forwardRef(function ListboxComponent(props, ref) {
      const { children, role, ...other } = props;
      const itemCount = Array.isArray(children) ? children.length : 0;
      const itemHeight = withCheckBoxes ? 48 : 42;

      const rowHeight = ({ index }) => {
        const text = options?.[index]?.text?.length;
        return text > itemHeight + 100 ? text - 10 : itemHeight;
      };

      // Only scroll to index if a selection was just made
      const scrollToIdx = shouldScrollToIndexRef.current ? focusedIndex : undefined;
      // Reset the flag after reading it
      if (shouldScrollToIndexRef.current) {
        shouldScrollToIndexRef.current = false;
      }

      return (
        <div ref={ref}>
          <div {...other}>
            <List
              height={children.length > 7 ? 250 : children.length * itemHeight}
              width={300}
              autoWidth={true}
              scrollToIndex={scrollToIdx}
              scrollToAlignment="start"
              className={'w-100 react-virtualized-override'}
              // rowHeight={itemHeight}
              rowHeight={rowHeight}
              overscanCount={5}
              rowCount={itemCount}
              rowRenderer={(props) => {
                return React.cloneElement(children[props.index], {
                  style: { ...props.style },
                });
              }}
              role={role}
            />
          </div>
        </div>
      );
    });
  }, [withCheckBoxes, options, focusedIndex]);

  const debouncedFetchQueriesOrPagesData = useMemo(
    () =>
      debounce(async (type, searchText) => {
        setLoadingOptions(true);
        const data = await getQueriesPagesData(type, searchText, schemaName, useSimpleExplores);
        if (data) {
          setLoadingOptions(false);
          setOptionsData(data);
        }
      }, 1500),
    [schemaName]
  );

  const fetchQueriesOrPagesData = useCallback(
    (type, searchText) => {
      debouncedFetchQueriesOrPagesData(type, searchText);
    },
    [debouncedFetchQueriesOrPagesData]
  );

  const debouncedFetchTopQueriesOrPages = useMemo(
    () =>
      debounce(async (type, searchText) => {
        setLoadingOptions(true);
        let data;
        data = await getTopQueriesORPages(type, searchText, false, schemaName);
        setOptionsData(data);
        setLoadingOptions(false);
      }, 1500),
    [schemaName]
  );
  const fetchTopQueriesOrPages = useCallback(
    (type, searchText) => {
      debouncedFetchTopQueriesOrPages(type, searchText);
    },
    [debouncedFetchTopQueriesOrPages]
  );

  const debouncedFetchCampaignKeywords = useMemo(
    () =>
      debounce(async (searchText) => {
        setLoadingOptions(true);
        const data = await getCampaignKeywordsData(searchText, schemaName);
        setOptionsData(data || []);
        setLoadingOptions(false);
      }, 500),
    [schemaName]
  );

  const fetchCampaignKeywords = useCallback(
    (searchText) => {
      debouncedFetchCampaignKeywords(searchText);
    },
    [debouncedFetchCampaignKeywords]
  );

  useEffect(() => {
    if (!Array.isArray(options)) return;

    let overridenOptions = [...options];

    if (!containsHierarchie && value && Array.isArray(value)) {
      const selectedOnes = [];
      const unSelectedOnes = [];

      overridenOptions.forEach((option) => {
        if (value.find((i) => i?.text === option?.text)) {
          selectedOnes.push(option);
        } else {
          unSelectedOnes.push(option);
        }
      });

      overridenOptions = [...selectedOnes, ...unSelectedOnes];
    }

    setOptionsData(overridenOptions);
  }, [containsHierarchie, options, value]);

  const focusOnInput = () => {
    const element = document.getElementById(`form-input-${type}`);
    if (element) {
      setTimeout(() => element.focus(), 1000);
    }
  };

  // A single-select field may hold its value wrapped in a one-item array (see `isArrayOpType`).
  const showSingleValueChip = singleValueAsChip && !multiple;
  const singleChipValue = showSingleValueChip ? (Array.isArray(value) ? value[0] : value) : null;
  const singleChipText =
    singleChipValue !== null && typeof singleChipValue === 'object'
      ? singleChipValue[useKeyForLabel] || singleChipValue.text || singleChipValue.value
      : singleChipValue;
  const singleChipLabel = singleChipText ? String(singleChipText).replace(/\/\*dash\*\//g, '-') : '';

  return (
    <Autocomplete
      id={'autocomplete'}
      // open={true}
      getOptionDisabled={disableAndUngroupChecker}
      disableClearable={multiple || loading || disableClearable}
      classes={{
        inputRoot: 'inputRoot-override',
        listbox: 'autocomplete-listbox-wrapper',
      }}
      disableListWrap
      ListboxComponent={
        !disableVirtualization && !getOptionNote && Array.isArray(optionsData) && optionsData.length > 100
          ? ListboxComponent
          : undefined
      }
      ChipProps={{
        className: 'chips-max-width',
      }}
      onChange={(e, res, test, lastOptions) => {
        e.preventDefault();
        const findIndex = optionsData.findIndex((op) => op?.value === lastOptions?.option?.value);
        const selectedIndex = findIndex >= 0 && findIndex > 2 ? findIndex - 2 : findIndex;
        // Only scroll to index when a selection is made
        shouldScrollToIndexRef.current = true;
        setIndex(selectedIndex || 0);
        if (!isOpen && prevSelected && res && prevSelected.length > res.length) {
          onClearViaChips(res);
        } else {
          // keyCode 8 = Backspace removing a chip; treated like clicking the chip's delete icon when opted in.
          if (e.keyCode != 8 || removeChipsOnBackspace) {
            onChange(isArrayOpType && res ? [res] : res);
            focusOnInput();
          }
        }
        if (type !== 'cr_page') {
          setSearchValue('');
        }
      }}
      loading={loadingOptions}
      disableCloseOnSelect={disableCloseOnSelect}
      freeSolo={(!withCheckBoxes && !isInternalLinking) || loading}
      renderTags={
        customTags
          ? () => {
              return <span className="custom-tag">{showAlternatePlaceholderWithoutOpen ? '' : customTags}</span>;
            }
          : undefined
      }
      multiple={multiple}
      forcePopupIcon={emptyHint ? true : 'auto'}
      popupIcon={emptyHint ? <span className="picker-down-arrow" /> : undefined}
      openOnFocus={true}
      onOpen={() => setOpen(true)}
      onClose={() => {
        if (onClose) {
          onClose();
        }
        setOpen(false);
      }}
      options={optionsData}
      className={className}
      disabled={disabled}
      getOptionLabel={(option) =>
        showSingleValueChip ? (
          '' // The value is shown as a chip, so the input only ever carries the search text.
        ) : multiple ? (
          <span>
            {option?.filterType ? <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>{option?.filterType}: </span> : ''}
            {isSourceFilter
              ? option?.text?.replace(/allsources/g, 'All traffic sources')
              : useKeyForLabel
              ? (option?.[useKeyForLabel] || option?.text || option?.value)?.replace(/\/\*dash\*\//g, '-')
              : containsHierarchie
              ? option?.value?.replace(/\/\*dash\*\//g, '-')
              : option?.text?.replace(/\/\*dash\*\//g, '-')}
          </span>
        ) : useKeyForLabel ? (
          (option?.[useKeyForLabel] || option?.text || option?.value)?.replace(/\/\*dash\*\//g, '-')
        ) : containsHierarchie ? (
          option?.value?.replace(/\/\*dash\*\//g, '-')
        ) : (
          option?.text?.replace(/\/\*dash\*\//g, '-') ||
          (isArrayOpType && option && option?.length ? option[0].text?.replace(/\/\*dash\*\//g, '-') : '')
        )
      }
      filterOptions={(optionsData, state) => {
        let overridenOptions = [...optionsData];

        state.inputValue = searchValue;

        if (
          !disableFetch &&
          state.inputValue &&
          prevSearchText != state.inputValue &&
          ['pages', 'queries', 'topPages', 'topQueries', 'campaign_keyword'].includes(type)
        ) {
          prevSearchText = state.inputValue;
          if (['topPages', 'topQueries'].includes(type)) {
            fetchTopQueriesOrPages(type, state.inputValue);
          } else if (type === 'campaign_keyword') {
            fetchCampaignKeywords(state.inputValue);
          } else {
            fetchQueriesOrPagesData(type, state.inputValue);
          }
        }
        if (containsHierarchie && state.inputValue) {
          const newJson = JSON.parse(JSON.stringify(overridenOptions));
          //// TODO: to resolved after filter drdopdown list and checked unchecked issue
          ////  remove this line JSON.parse(JSON.stringify(overridenOptions));
          //// and use this line=> const newJson = overridenOptions;
          overridenOptions = [];
          newJson.forEach((i) => {
            if (
              (typeof i.value === 'string' &&
                i.value.toLowerCase().includes(state.inputValue && state.inputValue.toLowerCase())) ||
              (i.filterString &&
                i.filterString.toLowerCase().includes(state.inputValue && state.inputValue.toLowerCase()))
            ) {
              i.text = i.value;
              i.ignoreStyle = true;
              overridenOptions.push(i);
            }
          });
          // Rank matches so entries belonging to the matched Category (the
          // category itself + its subcategories, whose value starts with the
          // search text) show before subcategories of OTHER categories that
          // merely mention the text; then by hierarchy level within each group.
          const searchText = (state.inputValue || '').toLowerCase();
          const categoryLevelRank = { category: 0, subcategory1: 1, subcategory2: 2 };
          const getCategoryLevelRank = (option) =>
            option && Object.prototype.hasOwnProperty.call(categoryLevelRank, option.type)
              ? categoryLevelRank[option.type]
              : 3;
          const getCategoryMatchRank = (option) =>
            typeof option?.value === 'string' && option.value.toLowerCase().startsWith(searchText) ? 0 : 1;
          overridenOptions.sort(
            (a, b) =>
              getCategoryMatchRank(a) - getCategoryMatchRank(b) || getCategoryLevelRank(a) - getCategoryLevelRank(b)
          );
        } else {
          if (useFilterString) {
            overridenOptions = overridenOptions.filter(
              (i) =>
                i.filterString &&
                i.filterString.toLowerCase().includes(state.inputValue && state.inputValue.toLowerCase())
            );
          } else {
            if (searchByText) {
              overridenOptions = overridenOptions.filter(
                (i) =>
                  i &&
                  typeof i.text === 'string' &&
                  i.text.toLowerCase().includes(state.inputValue && state.inputValue.toLowerCase())
              );
            } else {
              overridenOptions = overridenOptions.filter(
                (i) =>
                  i &&
                  typeof i.value === 'string' &&
                  i.value.toLowerCase().includes(state.inputValue && state.inputValue.toLowerCase())
              );
            }
          }
        }
        return overridenOptions;
      }}
      value={multiple ? value || [] : value}
      style={{ width: fullWidth ? '100%' : 300 }}
      renderOption={(option, res) => {
        let { selected } = res;
        if (prevSelected && Array.isArray(prevSelected)) {
          if (isCategoryFilter) {
            selected = Boolean(
              prevSelected.find(
                (i) =>
                  option?.encodedValue?.toLowerCase() === i.encodedValue?.toLowerCase() ||
                  option?.value?.toLowerCase() === i.value?.toLowerCase()
              )
            );
          } else {
            selected = Boolean(prevSelected.find((i) => option?.text?.toLowerCase() === i.text?.toLowerCase()));
          }
        }
        let groupCheckBoxCondition = true;
        if (
          groupCheckBox &&
          option.type == 'lh-category-disabled' &&
          disableAndUngroupChecker &&
          disableAndUngroupChecker(option)
        ) {
          groupCheckBoxCondition = false;
        }
        if (withCheckBoxes && groupCheckBoxCondition) {
          return (
            <>
              <div
                id="content_recommendation__div__form_auto_complete_0"
                className={
                  option.type &&
                  !option.ignoreStyle &&
                  ['lh-category-disabled', 'subcategory2', 'subcategory1'].includes(option.type)
                    ? option.type
                    : 'normal'
                }
              >
                <div
                  id="content_recommendation__div__form_auto_complete_1"
                  className="align-items-Center flex-column"
                  style={{ alignItems: 'start' }}
                >
                  <div id="content_recommendation__div__form_auto_complete_2" className="flex-row">
                    <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                    <span id="content_recommendation__span__form_auto_complete_3" className="align-self-center">
                      {option?.text?.length > 200 ? `${option?.text?.slice(0, 100)}...` : option?.text}
                    </span>
                  </div>
                  {getOptionNote ? (
                    <span className="form-auto-complete-option-note">{getOptionNote(option)}</span>
                  ) : null}
                  {showProgress ? (
                    <Tooltip
                      title={
                        !showProgressBarWithDirectValue
                          ? ''
                          : labelForProgressBar
                          ? `${option?.[keyForProgressBar]} ${labelForProgressBar}`
                          : option?.[keyForProgressBar]
                      }
                    >
                      <LinearProgress
                        style={{
                          // marginRight: 8,
                          width: '150px',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          top: 0,
                        }}
                        variant="determinate"
                        value={option?.[keyForProgressBar] ? (option?.[keyForProgressBar] / max) * 100 : 0}
                      />
                    </Tooltip>
                  ) : null}
                </div>
              </div>
            </>
          );
        }
        return (
          <>
            <div
              id="content_recommendation__div__form_auto_complete_4"
              className={
                option.type &&
                !option.ignoreStyle &&
                ['lh-category-disabled', 'subcategory2', 'subcategory1'].includes(option.type)
                  ? option.type
                  : 'normal'
              }
            >
              {option.text}
              {showProgress ? (
                <LinearProgress
                  style={{
                    width: '150px',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    top: 0,
                  }}
                  variant="determinate"
                  value={option?.[keyForProgressBar] ? (option?.[keyForProgressBar] / max) * 100 : 0}
                />
              ) : null}
            </div>
          </>
        );
      }}
      renderInput={(params) => {
        if (searchValue) {
          params.inputProps.value = searchValue;
        }
        return (
          <>
            <TextField
              onChange={(e) => setSearchValue(e.target.value)}
              {...params}
              id={type ? `form-input-${type}` : undefined}
              label={!hideLabel ? label : undefined}
              InputLabelProps={emptyHint ? { ...params.InputLabelProps, shrink: true } : params.InputLabelProps}
              placeholder={
                emptyHint && !(Array.isArray(value) ? value.length : value)
                  ? emptyHint
                  : hideLabel &&
                    (!isOpen && alternatePlaceholder && !showAlternatePlaceholderWithoutOpen
                      ? ''
                      : (isOpen || showAlternatePlaceholderWithoutOpen) && alternatePlaceholder
                      ? alternatePlaceholder
                      : label)
              }
              variant={variant || 'outlined'}
              fullWidth
              onBlur={(e) => {
                setSearchValue('');
                if (onTextBlur) {
                  onTextBlur(e.target.value);
                }
              }}
              onKeyUp={(e) => {
                if (onTextBlur && e.keyCode === 13) {
                  e.preventDefault();
                  setLoadingOptions(false);
                  if (e.type === 'keyup') {
                    setSearchValue('');
                    setLoadingOptions(false);
                  } else {
                    setSearchValue(e.target.value);
                  }
                  onTextBlur(e.target.value);
                }
              }}
              onKeyDown={(e) => {
                if (stopTyping) {
                  e.preventDefault();
                  return false;
                }
              }}
              InputProps={{
                ...params.InputProps,
                id: type ? `form-input-${type}` : params.InputProps.id,
                ...(singleChipLabel
                  ? { startAdornment: <Chip className="MuiAutocomplete-tag chips-max-width" label={singleChipLabel} /> }
                  : {}),
                endAdornment: (
                  <>
                    {loadingOptions ? <CircularProgress style={{ color: '#2E8ECF' }} size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              // eslint-disable-next-line
              autoFocus={EnableautoFocus}
            />
          </>
        );
      }}
    />
  );
}
